/**
 * ONE FORM: the household, the first person and the first home, together.
 *
 * This is deliberate and it matches the API, which creates all three in a single audited batch.
 * `api/src/routes/clients.ts` says why in one sentence: typing a household in three screens is how
 * a client ends up in the system with no phone number, and a client with no phone number cannot be
 * told a visit moved. Splitting this form is the same defect wearing a nicer interaction.
 *
 * Three fields carry more weight than their size suggests:
 *
 *   SOURCE CHANNEL is required, with no default, because the server refuses a client without one.
 *   Priority 7 of the brief is "say where clients came from", and a defaulted answer is a wrong
 *   answer that looks like a right one. The whole vocabulary is offered here, `website_form`
 *   included and warned about: at twenty households somebody will have filled the form while the
 *   seam was down, and filing her under `other` corrupts the exact count priority 7 produces. On a
 *   referral the introducer is chosen out of the client book, as an id and never as a name typed
 *   into the words, because about $107 a household is budgeted against that channel and a
 *   referral that cannot be traced to anybody is a payment nobody can make.
 *
 *   COUNTY is two counties and not one. Every ZIP in the service area is Pinellas except 34655,
 *   which is Pasco, and getting it wrong in writing to a property manager costs credibility.
 *
 *   THE DO-NOT LIST gets its own field, its own label and its own colour, because it is the single
 *   most common cause of a complaint in this category and it is worth asking for on day one. It,
 *   the pets and the way to the door are what home shows her of her home, so each box shows the
 *   claims check's coral and amber lines as it is typed, and a refusal of its words is said under it
 *   (ops #68).
 *
 * No price is typed here. The published card is shown, read only, from GET /api/catalog.
 *
 * AND IT CAN ARRIVE CARRYING AN ENQUIRY, which is the repair for the one way a household ends up
 * standing beside her own enquiry instead of being it. `?enquiry=<id>` is what the enquiries screen
 * links to as "Type her in as a household instead", for the row that needs the whole form rather
 * than the short conversion one: the do-not list, the pets, the way to the door, and what she
 * agreed to. `POST /api/clients` has always taken `enquiryId` and nothing in the interface sent it,
 * so `docs/handover.md` section 3 item 6 recorded the repair as reachable over signed HTTP and no
 * other way. Claiming it here files the two as one record: the office list links her, report A
 * counts her cohort, and the website is told what became of the lead it minted.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  CHANNELS,
  CONTACT_ROLES,
  COUNTIES,
  PREFERRED_CHANNELS,
  humanise,
  type CaptureMethod,
  type Channel,
  type ContactRole,
  type County,
  type PreferredChannel,
} from '@fk/shared';
import {
  ApiError,
  createClient,
  getCatalog,
  listEnquiries,
  type Catalog,
  type EnquiryRow,
} from '../api.ts';
import { Card, ScreenTitle } from '../components/AppShell.tsx';
import { ChecksFirstBoxes, joinChecksFirst } from '../components/ChecksFirstBoxes.tsx';
import { Button, SelectField, TextAreaField, TextField } from '../components/Field.tsx';
import { ErrorNotice } from '../components/Notice.tsx';
import { PublishedRates, rowLabelForHome } from '../components/PublishedRates.tsx';
import { SourceFields } from '../components/SourceFields.tsx';
import { WordsCheck, refusedFieldsOf, useRefusedBoxes } from '../components/WordsCheck.tsx';
import {
  CONSENT_CHOICES,
  OFFICE_CAPTURE_METHODS,
  TEXT_CONSENT_NOTE,
  consentFromAnswer,
  consentProblems,
  type ConsentAnswer,
  type ConsentChoice,
} from '../consent.ts';
import { E164, formatInstant, formatPhone, orNull, toE164, wholeNumber } from '../format.ts';
import { CAPTURE_METHOD_LABELS, CHANNEL_LABELS } from '../labels.ts';

/** The one ZIP in the service area that is not Pinellas. */
const PASCO_ZIP = '34655';

/** Above what the claims check found in one of the home's boxes, which home shows her (ops #68). */
const HOME_WORDS_REFUSED =
  'Not saved. They read this on their page at home, so change the words marked here and save it again.';

interface Draft {
  displayName: string;
  sourceChannel: Channel | '';
  sourceDetail: string;
  /** Chosen out of the client book on a referral, and cleared by any other word. */
  referredByClientId: string | null;
  contactName: string;
  contactRole: ContactRole;
  contactPhone: string;
  contactEmail: string;
  contactPreferred: PreferredChannel | '';
  siteLabel: string;
  addressLine1: string;
  addressLine2: string;
  town: string;
  postalCode: string;
  county: County;
  communityName: string;
  bedrooms: string;
  bathrooms: string;
  squareFeet: string;
  doNotList: string;
  pets: string;
  entryNotes: string;
  /** The three things they check first, one a box (ops #86). */
  checksFirst: string[];
}

const BLANK: Draft = {
  displayName: '',
  sourceChannel: '',
  sourceDetail: '',
  referredByClientId: null,
  contactName: '',
  contactRole: 'primary',
  contactPhone: '',
  contactEmail: '',
  contactPreferred: '',
  siteLabel: '',
  addressLine1: '',
  addressLine2: '',
  town: '',
  postalCode: '',
  county: 'pinellas',
  communityName: '',
  bedrooms: '',
  bathrooms: '',
  squareFeet: '',
  doNotList: '',
  pets: '',
  entryNotes: '',
  checksFirst: ['', '', ''],
};

const NOTHING_ASKED: ConsentAnswer = {
  agreed: false,
  choice: 'email',
  captureMethod: 'verbal_on_call',
  words: '',
};

/**
 * WHAT THE ENQUIRY ALREADY KNOWS, and nothing it does not.
 *
 * Only blank boxes are filled, so a slow read cannot land on top of something somebody has already
 * typed, and every box stays changeable afterwards: the enquiry is where she came from, not the
 * authority on how her household is spelled. The county follows the ZIP by the same rule the form
 * follows everywhere else, because 34655 is the one Pasco ZIP in the service area.
 *
 * THE INTRODUCER COMES ACROSS AS AN ID, which is the field it would be most expensive to lose.
 * About $107 a household is budgeted against the referral channel, and a referral that arrives on
 * an enquiry with a household behind it and reaches the client book with nothing behind it is a
 * thank-you nobody can make and a payment nobody can explain.
 */
export function prefillFrom(draft: Draft, row: EnquiryRow): Draft {
  const blank = (typed: string, from: string | null): string =>
    typed.trim() === '' ? (from ?? '') : typed;
  return {
    ...draft,
    displayName: blank(draft.displayName, row.name),
    sourceChannel: draft.sourceChannel === '' ? row.sourceChannel : draft.sourceChannel,
    sourceDetail: blank(draft.sourceDetail, row.sourceDetail),
    referredByClientId: draft.referredByClientId ?? row.referredByClientId,
    contactName: blank(draft.contactName, row.name),
    contactPhone: blank(draft.contactPhone, formatPhone(row.phoneE164)),
    contactEmail: blank(draft.contactEmail, row.email),
    town: blank(draft.town, row.town),
    postalCode: blank(draft.postalCode, row.zip),
    county: row.zip?.trim() === PASCO_ZIP ? 'pasco' : draft.county,
  };
}

/**
 * Everything wrong with the form, in the order the fields appear, in sentences somebody can act
 * on. The server checks all of this again and is the authority; this exists so a person on a phone
 * finds out before the round trip rather than after it.
 */
function problemsWith(draft: Draft): string[] {
  const problems: string[] = [];
  if (!draft.displayName.trim()) problems.push('The household needs a name, like "the Harrisons".');
  if (!draft.sourceChannel) {
    problems.push('Choose how this household was found. It cannot be left out.');
  }
  if (!draft.contactName.trim()) problems.push('The person to ring needs a name.');
  if (draft.contactPhone.trim() && !E164.test(toE164(draft.contactPhone) ?? '')) {
    problems.push(
      'That phone number does not look right. Ten digits is enough, like 727 337 7775.',
    );
  }
  if (draft.contactEmail.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(draft.contactEmail.trim())) {
    problems.push('That email address does not look right.');
  }
  if (!draft.addressLine1.trim()) problems.push('The home needs a street address.');
  if (!draft.town.trim()) problems.push('The home needs a town.');
  if (draft.postalCode.trim().length < 5) problems.push('The home needs a five-digit ZIP.');
  for (const [label, typed] of [
    ['Bedrooms', draft.bedrooms],
    ['Bathrooms', draft.bathrooms],
    ['Square feet', draft.squareFeet],
  ] as const) {
    if (wholeNumber(typed) === undefined) problems.push(`${label} has to be a whole number.`);
  }
  return problems;
}

export function NewClient() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const wantedEnquiryId = search.get('enquiry');
  const [draft, setDraft] = useState<Draft>(BLANK);
  const [consent, setConsent] = useState<ConsentAnswer>(NOTHING_ASKED);
  const [problems, setProblems] = useState<readonly string[]>([]);
  const [failure, setFailure] = useState<ApiError | null>(null);
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [enquiry, setEnquiry] = useState<EnquiryRow | null>(null);
  const [enquiryProblem, setEnquiryProblem] = useState<string | null>(null);
  const [claiming, setClaiming] = useState(true);
  // What the claims check refused in the home's words, by the request's names (ops #68).
  const boxes = useRefusedBoxes();

  /**
   * THE ENQUIRY THIS FORM ARRIVED CARRYING, if it arrived carrying one at all.
   *
   * There is no read of a single enquiry, so this is the office list, once, and the row is found
   * in it. Closed rows are asked for on purpose: an enquiry somebody marked lost and then thought
   * better of is exactly the one being typed in by hand, and a list that left it out would say the
   * id names nothing.
   */
  useEffect(() => {
    if (!wantedEnquiryId) return;
    let live = true;
    listEnquiries(true)
      .then((answer) => {
        if (!live) return;
        const found = answer.enquiries.find((row) => row.id === wantedEnquiryId) ?? null;
        if (!found) {
          setEnquiryProblem(
            'That enquiry is not on the list any more, so this household will not be filed against one. Everything else on this form still works.',
          );
          return;
        }
        setEnquiry(found);
        setDraft((previous) => prefillFrom(previous, found));
      })
      .catch(() => {
        if (!live) return;
        setEnquiryProblem(
          'Their enquiry did not load, so nothing has been filled in from it and this household will not be filed against one.',
        );
      });
    return () => {
      live = false;
    };
  }, [wantedEnquiryId]);

  useEffect(() => {
    let live = true;
    getCatalog()
      .then((answer) => {
        if (live) setCatalog(answer);
      })
      .catch(() => {
        // The card is a reference on this screen and nothing depends on it. A form that refuses to
        // open because a price list did not load would be a worse product than one without it.
      });
    return () => {
      live = false;
    };
  }, []);

  const set = <K extends keyof Draft>(field: K, value: Draft[K]) =>
    setDraft((previous) => ({ ...previous, [field]: value }));

  /** A word typed in one of the home's three boxes takes that box's refusal away. */
  const homeWords = (field: 'doNotList' | 'pets' | 'entryNotes', value: string) => {
    set(field, value);
    boxes.clear(`site.${field}`);
  };

  const zipDisagrees =
    (draft.postalCode.trim() === PASCO_ZIP && draft.county !== 'pasco') ||
    (draft.postalCode.trim().length === 5 &&
      draft.postalCode.trim() !== PASCO_ZIP &&
      draft.county === 'pasco');

  const highlightRowLabel = catalog
    ? rowLabelForHome(
        catalog.lines,
        wholeNumber(draft.bedrooms) ?? null,
        wholeNumber(draft.bathrooms) ?? null,
      )
    : null;

  /**
   * An enquiry that is already a household is never claimed a second time. The server refuses it
   * with a 409 naming the household that exists, and this says the same thing before the round
   * trip, with the link, because opening her is what somebody in that position actually wants.
   */
  const alreadyAHousehold = enquiry !== null && enquiry.clientId !== null;
  const claimedEnquiryId = enquiry !== null && claiming && !alreadyAHousehold ? enquiry.id : null;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setFailure(null);
    boxes.setRefused({});
    const found = [...problemsWith(draft), ...consentProblems(consent, draft.contactEmail)];
    setProblems(found);
    if (found.length > 0 || !draft.sourceChannel) {
      window.scrollTo({ top: 0 });
      return;
    }

    setSaving(true);
    try {
      const created = await createClient({
        displayName: draft.displayName.trim(),
        sourceChannel: draft.sourceChannel,
        sourceDetail: orNull(draft.sourceDetail),
        ...(claimedEnquiryId === null ? {} : { enquiryId: claimedEnquiryId }),
        // The credit follows the word: on any channel but a referral there is nobody to credit,
        // and a household credited under one of the others is a payment nobody can explain.
        referredByClientId: draft.sourceChannel === 'referral' ? draft.referredByClientId : null,
        contact: {
          role: draft.contactRole,
          displayName: draft.contactName.trim(),
          phoneE164: draft.contactPhone.trim() ? toE164(draft.contactPhone) : null,
          email: orNull(draft.contactEmail),
          preferredChannel: draft.contactPreferred ? draft.contactPreferred : null,
          // Nothing at all when nobody has asked her, because an unasked question is not a
          // refusal and the register it would land in has no edit path.
          consent: consentFromAnswer(consent),
        },
        site: {
          label: orNull(draft.siteLabel),
          addressLine1: draft.addressLine1.trim(),
          addressLine2: orNull(draft.addressLine2),
          town: draft.town.trim(),
          postalCode: draft.postalCode.trim(),
          county: draft.county,
          communityName: orNull(draft.communityName),
          bedrooms: wholeNumber(draft.bedrooms) ?? null,
          bathrooms: wholeNumber(draft.bathrooms) ?? null,
          squareFeet: wholeNumber(draft.squareFeet) ?? null,
          doNotList: orNull(draft.doNotList),
          pets: orNull(draft.pets),
          entryNotes: orNull(draft.entryNotes),
          checksFirst: joinChecksFirst(draft.checksFirst),
        },
      });
      navigate(`/clients/${created.id}`);
    } catch (thrown) {
      if (thrown instanceof ApiError) boxes.setRefused(refusedFieldsOf(thrown.body));
      setFailure(
        thrown instanceof ApiError
          ? thrown
          : new ApiError(0, 'error', 'That client was not saved.'),
      );
      window.scrollTo({ top: 0 });
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={submit} noValidate>
      <ScreenTitle
        title="A new client"
        subtitle="The household, the person to ring and the home, in one go."
      />

      {failure ? (
        <div className="mb-4">
          <ErrorNotice title="Nothing was saved" message={failure.message} />
        </div>
      ) : null}

      {problems.length > 0 ? (
        <div
          className="mb-4 rounded-xl border border-coral/50 border-l-4 border-l-coral bg-parchment p-4"
          role="alert"
        >
          <h2 className="font-serif text-lg">Before this can be saved</h2>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
            {problems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {enquiryProblem ? (
        <div className="mb-4">
          <ErrorNotice title="Their enquiry" message={enquiryProblem} />
        </div>
      ) : null}

      {enquiry ? (
        <HerEnquiry
          row={enquiry}
          claiming={claiming}
          alreadyAHousehold={alreadyAHousehold}
          onClaiming={setClaiming}
        />
      ) : null}

      <Card className="mb-5">
        <h2 className="mb-3 font-serif text-lg">The household</h2>
        <TextField
          id="display-name"
          label="Household"
          required
          value={draft.displayName}
          onChange={(value) => set('displayName', value)}
          hint='As it is spoken about, "the Harrisons", not a legal name.'
          autoComplete="off"
        />
        <SourceFields
          idPrefix="new-client"
          channels={CHANNELS}
          value={{
            sourceChannel: draft.sourceChannel,
            sourceDetail: draft.sourceDetail,
            referredByClientId: draft.referredByClientId,
          }}
          onChange={(next) =>
            setDraft((previous) => ({
              ...previous,
              sourceChannel: next.sourceChannel,
              sourceDetail: next.sourceDetail,
              referredByClientId: next.referredByClientId,
            }))
          }
          channelLabel="How were they found"
          channelPlaceholder="Choose one"
          required
          channelHint="Required, and never guessed. With a handful of clients this is the only honest read on which channel is worth money."
          detailHint="Which Nextdoor thread, which realtor's office, which letter batch. This is what the search box looks at."
        />
      </Card>

      <Card className="mb-5">
        <h2 className="mb-3 font-serif text-lg">The person to ring</h2>
        <TextField
          id="contact-name"
          label="Name"
          required
          value={draft.contactName}
          onChange={(value) => set('contactName', value)}
          autoComplete="off"
        />
        <SelectField
          id="contact-role"
          label="Who they are to the household"
          value={draft.contactRole}
          onChange={(value) => set('contactRole', value)}
          options={CONTACT_ROLES}
          toLabel={humanise}
        />
        <TextField
          id="contact-phone"
          label="Phone"
          type="tel"
          inputMode="tel"
          value={draft.contactPhone}
          onChange={(value) => set('contactPhone', value)}
          hint="Ten digits is enough. Without a number this household cannot be told when a visit moves."
          autoComplete="off"
        />
        <TextField
          id="contact-email"
          label="Email"
          type="email"
          inputMode="email"
          value={draft.contactEmail}
          onChange={(value) => set('contactEmail', value)}
          autoComplete="off"
        />
        <SelectField
          id="contact-preferred"
          label="How they want to hear from us"
          value={draft.contactPreferred}
          onChange={(value) => set('contactPreferred', value)}
          options={PREFERRED_CHANNELS}
          toLabel={humanise}
          placeholder="Nobody has asked yet"
          hint='"Call, do not text" decides whether telling them about a change feels like being told at all.'
        />
        <WhatSheAgreedTo answer={consent} onChange={setConsent} />
      </Card>

      <Card className="mb-5">
        <h2 className="mb-3 font-serif text-lg">The home</h2>
        <TextField
          id="site-label"
          label="What the household calls it"
          value={draft.siteLabel}
          onChange={(value) => set('siteLabel', value)}
          hint='Only needed when there is more than one home, "the Trinity house".'
          autoComplete="off"
        />
        <TextField
          id="address1"
          label="Street address"
          required
          value={draft.addressLine1}
          onChange={(value) => set('addressLine1', value)}
          autoComplete="off"
        />
        <TextField
          id="address2"
          label="Unit, apartment or villa number"
          value={draft.addressLine2}
          onChange={(value) => set('addressLine2', value)}
          hint="A missing unit number is somebody at the wrong door."
          autoComplete="off"
        />
        <TextField
          id="town"
          label="Town"
          required
          value={draft.town}
          onChange={(value) => set('town', value)}
          autoComplete="off"
        />
        <TextField
          id="zip"
          label="ZIP"
          required
          inputMode="numeric"
          value={draft.postalCode}
          onChange={(value) => set('postalCode', value)}
          autoComplete="off"
        />
        <SelectField
          id="county"
          label="County"
          required
          value={draft.county}
          onChange={(value) => set('county', value)}
          options={COUNTIES}
          toLabel={humanise}
          hint="34655 is Pasco. Every other ZIP in the service area is Pinellas."
        />
        {zipDisagrees ? (
          <p className="mb-4 rounded-lg border border-coral/50 border-l-4 border-l-coral bg-parchment px-3 py-2 text-sm">
            That ZIP and that county do not agree. 34655 is Pasco, and the rest is Pinellas.
          </p>
        ) : null}
        <TextField
          id="community"
          label="Gated community"
          value={draft.communityName}
          onChange={(value) => set('communityName', value)}
          hint="The gate they will be stopped at. Vendor registration is filed in the community's name, not the household's."
          autoComplete="off"
        />
        <div className="grid grid-cols-3 gap-3">
          <TextField
            id="bedrooms"
            label="Beds"
            inputMode="numeric"
            value={draft.bedrooms}
            onChange={(value) => set('bedrooms', value)}
          />
          <TextField
            id="bathrooms"
            label="Baths"
            inputMode="numeric"
            value={draft.bathrooms}
            onChange={(value) => set('bathrooms', value)}
          />
          <TextField
            id="square-feet"
            label="Sq ft"
            inputMode="numeric"
            value={draft.squareFeet}
            onChange={(value) => set('squareFeet', value)}
          />
        </div>
        <p className="mb-4 text-xs text-navy/65">
          Beds and baths are what put a home on a row of the published card. Square feet is what
          home watch is priced by.
        </p>
      </Card>

      <Card className="mb-5">
        <h2 className="font-serif text-lg">What this home needs</h2>
        <p className="mt-1 mb-3 text-sm text-navy/70">
          The do-not list is asked for on day one. Touching something the owner did not want touched
          is the most common way a recurring contract ends. Where to park, how to get in and the
          three things they check first go on the cleaner's sheet beside it.
        </p>
        <TextAreaField
          id="do-not-list"
          label="Do not touch"
          emphasis
          rows={4}
          value={draft.doNotList}
          onChange={(value) => homeWords('doNotList', value)}
          hint="One line each. The office desk, the grandmother's china, the closed door at the end of the hall."
        />
        <WordsCheck
          words={draft.doNotList}
          refused={boxes.refused['site.doNotList']}
          refusedTitle={HOME_WORDS_REFUSED}
        />
        <TextAreaField
          id="pets"
          label="Pets"
          value={draft.pets}
          onChange={(value) => homeWords('pets', value)}
          hint="A dog nobody mentioned is a visit that ends on the driveway."
        />
        <WordsCheck
          words={draft.pets}
          refused={boxes.refused['site.pets']}
          refusedTitle={HOME_WORDS_REFUSED}
        />
        <TextAreaField
          id="entry-notes"
          label="Getting to the door"
          value={draft.entryNotes}
          onChange={(value) => homeWords('entryNotes', value)}
          hint="Ask where to park and how to get in: park on the street, the side gate sticks, bins on the left. Anything that actually opens something is held encrypted and is not typed here."
        />
        <WordsCheck
          words={draft.entryNotes}
          refused={boxes.refused['site.entryNotes']}
          refusedTitle={HOME_WORDS_REFUSED}
        />
        <ChecksFirstBoxes
          idPrefix="checks-first"
          lines={draft.checksFirst}
          onChange={(lines) => {
            set('checksFirst', lines);
            boxes.clear('site.checksFirst');
          }}
          refused={boxes.refused['site.checksFirst']}
        />
      </Card>

      {catalog ? (
        <details className="mb-5 rounded-xl border border-navy/12 bg-parchment p-4">
          <summary className="min-h-11 desk:min-h-9 cursor-pointer font-serif text-lg">
            The published rates, for reading out
          </summary>
          <div className="mt-3">
            <PublishedRates
              lines={catalog.lines}
              sourceUrl={catalog.sourceUrl}
              highlightRowLabel={highlightRowLabel}
            />
          </div>
        </details>
      ) : null}

      <div className="pb-4">
        <Button type="submit" full disabled={saving}>
          {saving ? 'Saving' : 'Save this client'}
        </Button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------ *
 * The enquiry this form arrived carrying.
 * ------------------------------------------------------------------ */

/**
 * WHAT SAVING THIS FORM DOES TO THE ROW ON THE ENQUIRIES LIST, said before it is done.
 *
 * Claiming is on by default, because somebody who followed a link out of that row meant to file
 * the two as one record. It can be dropped in one press all the same: the same household is
 * sometimes typed in from scratch while the enquiry stays open for somebody else to work, and a
 * claim nobody can decline is a claim made on their behalf.
 */
function HerEnquiry({
  row,
  claiming,
  alreadyAHousehold,
  onClaiming,
}: {
  row: EnquiryRow;
  claiming: boolean;
  alreadyAHousehold: boolean;
  onClaiming: (claiming: boolean) => void;
}) {
  const arrived = formatInstant(row.createdAt);

  if (alreadyAHousehold) {
    return (
      <Card className="mb-5">
        <h2 className="font-serif text-lg">Their enquiry is already a household</h2>
        <p className="mt-1 text-sm">
          {row.name} is in the book already, so nothing typed here will be filed against that
          enquiry. Open them rather than typing in a second one.
        </p>
        {row.clientId ? (
          <Link
            to={`/clients/${row.clientId}`}
            className="mt-3 inline-flex min-h-11 desk:min-h-9 items-center rounded-lg border border-navy/25 bg-cream px-4 text-sm font-medium"
          >
            Open their household
          </Link>
        ) : null}
      </Card>
    );
  }

  return (
    <Card className="mb-5">
      <h2 className="font-serif text-lg">Their enquiry</h2>
      <p className="mt-1 text-sm font-medium">{row.name}</p>
      {/* The channel is written as the enquiries list writes it, capital and all: half of these
          are proper nouns and lowercasing "Nextdoor" into a sentence spells it wrong. */}
      <p className="text-sm text-navy/75">
        {CHANNEL_LABELS[row.sourceChannel]}
        {arrived ? `, arrived ${arrived}` : ''}.
      </p>
      {claiming ? (
        <>
          <p className="mt-2 text-sm">
            Saving files this household as that enquiry. It is marked booked and leaves the list,
            the office list links them from it afterwards, and the website is told what became of
            the lead it sent.
          </p>
          <div className="mt-3">
            <Button tone="quiet" onClick={() => onClaiming(false)}>
              Type them in without the enquiry
            </Button>
          </div>
        </>
      ) : (
        <>
          <p className="mt-2 text-sm">
            This household will not be filed against that enquiry, and the row stays open on the
            list for somebody to work.
          </p>
          <div className="mt-3">
            <Button tone="quiet" onClick={() => onClaiming(true)}>
              File them against the enquiry after all
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ *
 * What she agreed to, asked on the form the number is typed on.
 * ------------------------------------------------------------------ */

/**
 * THE ONE MOMENT SOMEBODY IS CERTAINLY HOLDING THE ANSWER is the call this form is being typed
 * during, and until now the question was asked nowhere but the household page afterwards. So a
 * client typed in on Monday could not be told anything until somebody remembered to open her card
 * and record it, and the first sign of that was a move the register refused.
 *
 * IT IS WORDED THE WAY THE HOUSEHOLD PAGE WORDS IT, from the same list, because the two are the
 * same claim: what she ticks here is what that page will say she agreed to.
 *
 * UNTICKED SENDS NOTHING AT ALL. Nobody having asked her is not a refusal, and the register it
 * would land in is append-only, so a `withdrawn` row written for a question never put would be a
 * position she never took, kept for ever.
 *
 * EMAILS ONLY, SINCE OPS #51. A yes to texts is the box on the website quote form and nothing a
 * person types, so the question here is about emails, and the note says where texts come from.
 */
function WhatSheAgreedTo({
  answer,
  onChange,
}: {
  answer: ConsentAnswer;
  onChange: (answer: ConsentAnswer) => void;
}) {
  const set = <K extends keyof ConsentAnswer>(name: K, value: ConsentAnswer[K]) =>
    onChange({ ...answer, [name]: value });

  return (
    <div className="mb-4 rounded-lg border border-navy/15 p-3">
      <label
        htmlFor="consent-agreed"
        className="flex min-h-11 desk:min-h-9 cursor-pointer items-center gap-3 text-sm font-medium"
      >
        <input
          id="consent-agreed"
          type="checkbox"
          checked={answer.agreed}
          onChange={(event) => set('agreed', event.target.checked)}
          className="h-5 w-5 shrink-0 accent-navy"
        />
        <span>Agreed to be emailed about cleans</span>
      </label>
      <p className="mt-1 text-xs text-navy/65">
        Left as it is, nothing is recorded either way. Nobody having asked is not the same as their
        saying no, and until there is a row nothing may be sent to them at all.
      </p>
      <p id="consent-texts-note" className="mt-1 text-xs text-navy/65">
        {TEXT_CONSENT_NOTE}
      </p>

      {answer.agreed ? (
        <div className="mt-3">
          <fieldset>
            <legend className="text-sm font-medium">What they agreed to</legend>
            <div className="mt-2 flex flex-col gap-2">
              {CONSENT_CHOICES.map((option) => (
                <label
                  key={option.id}
                  htmlFor={`consent-${option.id}`}
                  className={`flex min-h-11 desk:min-h-9 cursor-pointer items-center gap-3 rounded-lg border p-3 ${
                    answer.choice === option.id ? 'border-navy bg-gold/10' : 'border-navy/25'
                  }`}
                >
                  <input
                    id={`consent-${option.id}`}
                    type="radio"
                    name="consent-purpose"
                    checked={answer.choice === option.id}
                    onChange={() => set('choice', option.id as ConsentChoice)}
                    className="h-5 w-5 shrink-0 accent-navy"
                  />
                  <span className="text-sm">{option.label}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="mt-3">
            <SelectField
              id="consent-how"
              label="How they gave it"
              value={answer.captureMethod}
              onChange={(value: CaptureMethod) => set('captureMethod', value)}
              options={OFFICE_CAPTURE_METHODS}
              toLabel={(value) => CAPTURE_METHOD_LABELS[value]}
            />
            <TextAreaField
              id="consent-words"
              label="Their words"
              value={answer.words}
              onChange={(value) => set('words', value)}
              rows={2}
              hint="What they actually said. For a consent given on a call this is the only evidence there will ever be."
            />
          </div>

          <p className="text-xs text-navy/65">
            Recorded as given now, on the server's own clock, because this is being typed while they
            are on the phone. A consent from an earlier day is written up on their household page,
            where the day it was given is asked for.
          </p>
        </div>
      ) : null}
    </div>
  );
}
